/**
 * pathwayGenerator.logic.ts
 * Pure domain logic for 3D cable tray graph synthesis, multi-tier elevation,
 * transverse spine collector generation, and orthogonal T-junction deduplication.
 *
 * Standards: ANSI/TIA-942-C, TIA-569-D, BICSI 002-2021, and NFPA 70.
 */

import {
  type PathwayPresetId,
  type PathwayAlignment,
  type SpineCollectorPosition,
  type PathwayLayerConfig,
  getPathwayPreset,
  calculateLayerAbsoluteZ,
} from './pathwayPresets.logic.ts';
import { type TrayMedia, type TrayConstruction } from './traySegregation.logic.ts';

export const DEFAULT_PPM = 100; // 100 canvas pixels per physical meter
const DEDUPLICATION_TOLERANCE_M = 0.01; // 1 cm tolerance for node deduplication

export interface PathwayGeneratorRack {
  id: string;
  x: number;               // canvas px
  y: number;               // canvas px
  width?: number;          // canvas px
  height?: number;         // canvas px
  widthM?: number | null;  // unknown dimensions use an estimate, not persisted rack data
  depthM?: number | null;
  heightM?: number | null; // unknown physical height uses the generator's explicit default
  orientation?: number;    // 0 | 90 | 180 | 270
  rotation?: number;       // fallback orientation
  rowId?: string;
  rowIndex?: number;
  rackIndex?: number;
  label?: string;
}

export interface PathwayGeneratorRow {
  id: string;
  name: string;
  orientation?: number;    // 0 | 90 | 180 | 270
  labelIndex?: number;
  color?: string | null;
}

export interface PathwayGeneratorOptions {
  racks: PathwayGeneratorRack[] | Record<string, PathwayGeneratorRack>;
  rows?: Record<string, PathwayGeneratorRow>;
  presetId?: PathwayPresetId;
  customLayers?: PathwayLayerConfig[];
  alignment?: PathwayAlignment;             // 'rear' | 'centerline' (default 'rear')
  overhangM?: number;                       // default 0.3m
  spineCollector?: SpineCollectorPosition;  // 'none' | 'head' | 'end' | 'both' (default 'head')
  rowIds?: string[];                        // optional filter to specific rows
  defaultRackHeightM?: number;              // default 2.0m
  ppm?: number;                             // default 100 (100 px per meter)
}

export interface GeneratedTrayNode {
  id: string;
  x: number;              // canvas px
  y: number;              // canvas px
  z_m: number;            // elevation in meters
  type: 'terminal' | 'tee';
}

export interface GeneratedTrayEdge {
  id: string;
  source: string;
  target: string;
  type: string;           // 'copper' | 'fiber' | 'power'
  mediaType: TrayMedia;
  trayType: TrayConstruction;
  color: string;
  widthM: number;
}

export interface PathwayGenerationMetrics {
  totalNodes: number;
  totalEdges: number;
  totalLengthM: number;
  lengthByMediaM: Record<string, number>;
  teeCount: number;
  terminalCount: number;
  connectedComponents: number;
  isFullyConnected: boolean;
}

export interface PathwayConnectivityResult {
  isFullyConnected: boolean;
  componentCount: number;
  unconnectedNodes: string[];
}

export interface PathwayGeneratorResult {
  nodes: Record<string, GeneratedTrayNode>;
  edges: Record<string, GeneratedTrayEdge>;
  metrics: PathwayGenerationMetrics;
}

interface AnalyzedRack {
  id: string;
  x_m: number;
  y_m: number;
  w_m: number;
  d_m: number;
  h_m: number;
  orientation: number;
  cx_m: number;
  cy_m: number;
  rx_m: number;
  ry_m: number;
}

interface AnalyzedRow {
  rowId: string;
  name: string;
  orientation: number;
  isVertical: boolean;
  racks: AnalyzedRack[];
  // Physical bounds in meters
  minCoord_m: number;
  maxCoord_m: number;
  inRowCoord_m: number; // constant Y if horizontal, constant X if vertical
  start_m: number;      // with overhang
  end_m: number;        // with overhang
}

/**
 * Normalizes input racks from array or record into array, filtering nullish items.
 */
function normalizeRacks(input?: PathwayGeneratorRack[] | Record<string, PathwayGeneratorRack> | null): PathwayGeneratorRack[] {
  if (!input) return [];
  const list = Array.isArray(input) ? input : Object.values(input);
  return list.filter((r): r is PathwayGeneratorRack => Boolean(r && typeof r === 'object' && typeof r.id === 'string'));
}

/**
 * Normalizes angle in degrees to nearest cardinal angle: 0 | 90 | 180 | 270.
 */
export function normalizeOrientation(deg: number): number {
  return ((Math.round(deg / 90) * 90) % 360 + 360) % 360;
}

/**
 * Computes rear cable entry point (rx, ry) for a rack per universal.py geometry.
 */
function calculateRackPoints(
  r: PathwayGeneratorRack,
  ppm: number,
  defaultH: number
): AnalyzedRack {
  const rawOrientation = r.orientation ?? r.rotation ?? 0;
  const orientation = normalizeOrientation(rawOrientation);
  const isVertical = orientation === 90 || orientation === 270;

  const w_m = r.widthM ?? (r.width ? r.width / ppm : 0.6);
  const d_m = r.depthM ?? (r.height ? r.height / ppm : 1.0);
  const h_m = r.heightM ?? defaultH;

  const x_m = r.x / ppm;
  const y_m = r.y / ppm;

  // When vertical (90/270), depth is along X and width is along Y
  const spanX_m = isVertical ? d_m : w_m;
  const spanY_m = isVertical ? w_m : d_m;

  const cx_m = x_m + spanX_m / 2;
  const cy_m = y_m + spanY_m / 2;

  let rx_m = cx_m;
  let ry_m = cy_m;

  if (orientation === 0) {
    // Front door faces South (+Y), rear faces North (-Y)
    rx_m = cx_m;
    ry_m = y_m;
  } else if (orientation === 180) {
    // Front door faces North (-Y), rear faces South (+Y)
    rx_m = cx_m;
    ry_m = y_m + spanY_m;
  } else if (orientation === 90) {
    // Front door faces West (-X), rear faces East (+X)
    rx_m = x_m + spanX_m;
    ry_m = cy_m;
  } else if (orientation === 270) {
    // Front door faces East (+X), rear faces West (-X)
    rx_m = x_m;
    ry_m = cy_m;
  }

  return {
    id: r.id,
    x_m,
    y_m,
    w_m: spanX_m,
    d_m: spanY_m,
    h_m,
    orientation,
    cx_m,
    cy_m,
    rx_m,
    ry_m,
  };
}

/**
 * Groups racks by row and analyzes spatial dimensions and alignment lines.
 */
function analyzeRows(
  racks: PathwayGeneratorRack[],
  rowsRecord: Record<string, PathwayGeneratorRow>,
  alignment: PathwayAlignment,
  overhangM: number,
  rowIdsFilter?: string[],
  defaultRackHeightM = 2.0,
  ppm = DEFAULT_PPM
): AnalyzedRow[] {
  const rackGroups = new Map<string, PathwayGeneratorRack[]>();

  for (const r of racks) {
    const rId = r.rowId || (r.rowIndex != null ? `row-${r.rowIndex}` : 'default-row');
    if (rowIdsFilter && rowIdsFilter.length > 0 && !rowIdsFilter.includes(rId)) {
      continue;
    }
    if (!rackGroups.has(rId)) {
      rackGroups.set(rId, []);
    }
    rackGroups.get(rId)!.push(r);
  }

  const analyzedRows: AnalyzedRow[] = [];

  for (const [rId, group] of rackGroups.entries()) {
    if (group.length === 0) continue;

    const rowConfig = rowsRecord[rId];
    const firstRack = group[0];
    const orientation = rowConfig?.orientation ?? firstRack.orientation ?? firstRack.rotation ?? 0;
    const isVertical = orientation === 90 || orientation === 270;

    const analyzedRacks = group.map(r => calculateRackPoints(r, ppm, defaultRackHeightM));

    // Sort racks in physical sequence along row direction
    if (isVertical) {
      analyzedRacks.sort((a, b) => a.cy_m - b.cy_m);
    } else {
      analyzedRacks.sort((a, b) => a.cx_m - b.cx_m);
    }

    let minCoord_m = Infinity;
    let maxCoord_m = -Infinity;
    let inRowCoord_m = 0;

    if (isVertical) {
      // Row runs along Y; constant coordinate is X
      for (const r of analyzedRacks) {
        if (r.y_m < minCoord_m) minCoord_m = r.y_m;
        if (r.y_m + r.d_m > maxCoord_m) maxCoord_m = r.y_m + r.d_m;
      }
      // Constant X line
      inRowCoord_m = alignment === 'rear'
        ? analyzedRacks[0].rx_m
        : analyzedRacks[0].cx_m;
    } else {
      // Row runs along X; constant coordinate is Y
      for (const r of analyzedRacks) {
        if (r.x_m < minCoord_m) minCoord_m = r.x_m;
        if (r.x_m + r.w_m > maxCoord_m) maxCoord_m = r.x_m + r.w_m;
      }
      // Constant Y line
      inRowCoord_m = alignment === 'rear'
        ? analyzedRacks[0].ry_m
        : analyzedRacks[0].cy_m;
    }

    const start_m = minCoord_m - overhangM;
    const end_m = maxCoord_m + overhangM;

    analyzedRows.push({
      rowId: rId,
      name: rowConfig?.name || `Row ${rId}`,
      orientation,
      isVertical,
      racks: analyzedRacks,
      minCoord_m,
      maxCoord_m,
      inRowCoord_m,
      start_m,
      end_m,
    });
  }

  return analyzedRows;
}

/**
 * Spatial Node Registry for exact coordinate deduplication (<= 1cm / 1px).
 */
class SpatialNodeRegistry {
  private nodes: Map<string, GeneratedTrayNode> = new Map();
  private ppm: number;

  constructor(ppm: number) {
    this.ppm = ppm;
  }

  /**
   * Finds or creates a node at the given coordinates.
   * If a node already exists within deduplication tolerance, reuses it and sets type to 'tee'.
   */
  getOrCreateNode(
    x_px: number,
    y_px: number,
    z_m: number,
    layerId: string,
    preferId?: string,
    initialType: 'terminal' | 'tee' = 'terminal'
  ): GeneratedTrayNode {
    const roundedX = Math.round(x_px);
    const roundedY = Math.round(y_px);
    const tolPx = Math.max(1, Math.round(DEDUPLICATION_TOLERANCE_M * this.ppm));
    const tolPxSq = tolPx * tolPx;

    // Search for existing node in same layer (matching Z elevation) within tolerance
    for (const node of this.nodes.values()) {
      if (Math.abs(node.z_m - z_m) < 0.01) {
        const dx = node.x - roundedX;
        const dy = node.y - roundedY;
        if (dx * dx + dy * dy <= tolPxSq) {
          // Found existing node -> promote to 'tee' if needed
          if (initialType === 'tee') {
            node.type = 'tee';
          }
          return node;
        }
      }
    }

    // Create new node
    const id = preferId || `tn_${layerId}_${roundedX}_${roundedY}_${Math.round(z_m * 100)}`;
    const newNode: GeneratedTrayNode = {
      id,
      x: roundedX,
      y: roundedY,
      z_m,
      type: initialType,
    };
    this.nodes.set(id, newNode);
    return newNode;
  }

  getAllNodes(): Record<string, GeneratedTrayNode> {
    const result: Record<string, GeneratedTrayNode> = {};
    for (const [id, node] of this.nodes.entries()) {
      result[id] = node;
    }
    return result;
  }
}

/**
 * Synthesizes 3D cable tray pathways across rack rows.
 */
export function generatePathways(options: PathwayGeneratorOptions): PathwayGeneratorResult {
  const {
    racks: rawRacks,
    rows: rowsRecord = {},
    presetId = 'single_overhead',
    customLayers,
    alignment = 'rear',
    overhangM = 0.3,
    spineCollector = 'head',
    rowIds,
    defaultRackHeightM = 2.0,
    ppm = DEFAULT_PPM,
  } = options;

  const safePpm = typeof ppm === 'number' && Number.isFinite(ppm) && ppm > 0 ? ppm : DEFAULT_PPM;
  const safeOverhangM = typeof overhangM === 'number' && Number.isFinite(overhangM) ? Math.max(0, overhangM) : 0.3;
  const safeDefaultRackHeightM = typeof defaultRackHeightM === 'number' && Number.isFinite(defaultRackHeightM) && defaultRackHeightM > 0 ? defaultRackHeightM : 2.0;

  const racks = normalizeRacks(rawRacks);
  const analyzedRows = analyzeRows(racks, rowsRecord, alignment, safeOverhangM, rowIds, safeDefaultRackHeightM, safePpm);

  // Determine layers to generate
  let layers: PathwayLayerConfig[] = [];
  if (presetId === 'custom' && customLayers && customLayers.length > 0) {
    layers = customLayers;
  } else {
    const preset = getPathwayPreset(presetId);
    layers = preset.layers;
  }

  const allNodes: Record<string, GeneratedTrayNode> = {};
  const allEdges: Record<string, GeneratedTrayEdge> = {};

  let edgeCounter = 0;

  // Generate pathways layer by layer
  for (const layer of layers) {
    const registry = new SpatialNodeRegistry(safePpm);
    const layerZ = calculateLayerAbsoluteZ(layer, safeDefaultRackHeightM);
    const rowStartNodes: Record<string, GeneratedTrayNode> = {};
    const rowEndNodes: Record<string, GeneratedTrayNode> = {};

    // 1. Generate In-Row Pathways
    for (const row of analyzedRows) {
      const rowNodes: GeneratedTrayNode[] = [];

      if (row.isVertical) {
        // Vertical row: line along constant X = inRowCoord_m
        const x_px = Math.round(row.inRowCoord_m * safePpm);

        // Start overhang node
        const startY_px = Math.round(row.start_m * safePpm);
        const startNode = registry.getOrCreateNode(
          x_px,
          startY_px,
          layerZ,
          layer.id,
          `tn_${layer.id}_${row.rowId}_start`,
          'terminal'
        );
        rowNodes.push(startNode);
        rowStartNodes[row.rowId] = startNode;

        // In-row rack drop nodes
        for (let i = 0; i < row.racks.length; i++) {
          const r = row.racks[i];
          const rackY_px = Math.round(r.cy_m * safePpm);
          const dropNode = registry.getOrCreateNode(
            x_px,
            rackY_px,
            layerZ,
            layer.id,
            `tn_${layer.id}_${r.id}_drop`,
            'terminal'
          );
          rowNodes.push(dropNode);
        }

        // End overhang node
        const endY_px = Math.round(row.end_m * safePpm);
        const endNode = registry.getOrCreateNode(
          x_px,
          endY_px,
          layerZ,
          layer.id,
          `tn_${layer.id}_${row.rowId}_end`,
          'terminal'
        );
        rowNodes.push(endNode);
        rowEndNodes[row.rowId] = endNode;
      } else {
        // Horizontal row: line along constant Y = inRowCoord_m
        const y_px = Math.round(row.inRowCoord_m * safePpm);

        // Start overhang node
        const startX_px = Math.round(row.start_m * safePpm);
        const startNode = registry.getOrCreateNode(
          startX_px,
          y_px,
          layerZ,
          layer.id,
          `tn_${layer.id}_${row.rowId}_start`,
          'terminal'
        );
        rowNodes.push(startNode);
        rowStartNodes[row.rowId] = startNode;

        // In-row rack drop nodes
        for (let i = 0; i < row.racks.length; i++) {
          const r = row.racks[i];
          const rackX_px = Math.round(r.cx_m * safePpm);
          const dropNode = registry.getOrCreateNode(
            rackX_px,
            y_px,
            layerZ,
            layer.id,
            `tn_${layer.id}_${r.id}_drop`,
            'terminal'
          );
          rowNodes.push(dropNode);
        }

        // End overhang node
        const endX_px = Math.round(row.end_m * safePpm);
        const endNode = registry.getOrCreateNode(
          endX_px,
          y_px,
          layerZ,
          layer.id,
          `tn_${layer.id}_${row.rowId}_end`,
          'terminal'
        );
        rowNodes.push(endNode);
        rowEndNodes[row.rowId] = endNode;
      }

      // Connect in-row nodes sequentially
      for (let i = 0; i < rowNodes.length - 1; i++) {
        const u = rowNodes[i];
        const v = rowNodes[i + 1];
        if (u.id === v.id) continue; // Skip identical nodes

        const edgeId = `te_${layer.id}_${++edgeCounter}`;
        allEdges[edgeId] = {
          id: edgeId,
          source: u.id,
          target: v.id,
          type: layer.media,
          mediaType: layer.media,
          trayType: layer.construction,
          color: layer.color,
          widthM: layer.defaultWidthM,
        };
      }
    }

    // 2. Generate Transverse Spine Collectors (if requested and >= 2 parallel rows exist)
    if (spineCollector !== 'none' && analyzedRows.length >= 2) {
      // Group rows by orientation (horizontal vs vertical)
      const hRows = analyzedRows.filter(r => !r.isVertical);
      const vRows = analyzedRows.filter(r => r.isVertical);

      // 2a. Horizontal rows spine collectors (transverse lines along Y)
      if (hRows.length >= 2) {
        hRows.sort((a, b) => a.inRowCoord_m - b.inRowCoord_m);

        if (spineCollector === 'head' || spineCollector === 'both') {
          // Head spine: at minimum start_m
          const headX_m = Math.min(...hRows.map(r => r.start_m));
          const headX_px = Math.round(headX_m * safePpm);

          const junctionNodes: GeneratedTrayNode[] = [];
          for (const r of hRows) {
            const y_px = Math.round(r.inRowCoord_m * safePpm);
            const jNode = registry.getOrCreateNode(
              headX_px,
              y_px,
              layerZ,
              layer.id,
              `tn_${layer.id}_${r.rowId}_head_tee`,
              'tee'
            );
            junctionNodes.push(jNode);

            // Connect staggered row start to spine T-junction if not coinciding
            const startNode = rowStartNodes[r.rowId];
            if (startNode && startNode.id !== jNode.id) {
              const edgeId = `te_${layer.id}_ext_h_${++edgeCounter}`;
              allEdges[edgeId] = {
                id: edgeId,
                source: jNode.id,
                target: startNode.id,
                type: layer.media,
                mediaType: layer.media,
                trayType: layer.construction,
                color: layer.color,
                widthM: layer.defaultWidthM,
              };
            }
          }

          // Connect consecutive junction nodes along the head spine
          for (let i = 0; i < junctionNodes.length - 1; i++) {
            const u = junctionNodes[i];
            const v = junctionNodes[i + 1];
            if (u.id === v.id) continue;
            const edgeId = `te_${layer.id}_spine_h_${++edgeCounter}`;
            allEdges[edgeId] = {
              id: edgeId,
              source: u.id,
              target: v.id,
              type: layer.media,
              mediaType: layer.media,
              trayType: layer.construction,
              color: layer.color,
              widthM: layer.defaultWidthM,
            };
          }
        }

        if (spineCollector === 'end' || spineCollector === 'both') {
          // End spine: at maximum end_m
          const endX_m = Math.max(...hRows.map(r => r.end_m));
          const endX_px = Math.round(endX_m * safePpm);

          const junctionNodes: GeneratedTrayNode[] = [];
          for (const r of hRows) {
            const y_px = Math.round(r.inRowCoord_m * safePpm);
            const jNode = registry.getOrCreateNode(
              endX_px,
              y_px,
              layerZ,
              layer.id,
              `tn_${layer.id}_${r.rowId}_end_tee`,
              'tee'
            );
            junctionNodes.push(jNode);

            // Connect shorter row end to spine T-junction if not coinciding
            const endNode = rowEndNodes[r.rowId];
            if (endNode && endNode.id !== jNode.id) {
              const edgeId = `te_${layer.id}_ext_e_${++edgeCounter}`;
              allEdges[edgeId] = {
                id: edgeId,
                source: endNode.id,
                target: jNode.id,
                type: layer.media,
                mediaType: layer.media,
                trayType: layer.construction,
                color: layer.color,
                widthM: layer.defaultWidthM,
              };
            }
          }

          // Connect consecutive junction nodes along the end spine
          for (let i = 0; i < junctionNodes.length - 1; i++) {
            const u = junctionNodes[i];
            const v = junctionNodes[i + 1];
            if (u.id === v.id) continue;
            const edgeId = `te_${layer.id}_spine_e_${++edgeCounter}`;
            allEdges[edgeId] = {
              id: edgeId,
              source: u.id,
              target: v.id,
              type: layer.media,
              mediaType: layer.media,
              trayType: layer.construction,
              color: layer.color,
              widthM: layer.defaultWidthM,
            };
          }
        }
      }

      // 2b. Vertical rows spine collectors (transverse lines along X)
      if (vRows.length >= 2) {
        vRows.sort((a, b) => a.inRowCoord_m - b.inRowCoord_m);

        if (spineCollector === 'head' || spineCollector === 'both') {
          const headY_m = Math.min(...vRows.map(r => r.start_m));
          const headY_px = Math.round(headY_m * safePpm);

          const junctionNodes: GeneratedTrayNode[] = [];
          for (const r of vRows) {
            const x_px = Math.round(r.inRowCoord_m * safePpm);
            const jNode = registry.getOrCreateNode(
              x_px,
              headY_px,
              layerZ,
              layer.id,
              `tn_${layer.id}_${r.rowId}_head_tee`,
              'tee'
            );
            junctionNodes.push(jNode);

            // Connect staggered row start to spine T-junction if not coinciding
            const startNode = rowStartNodes[r.rowId];
            if (startNode && startNode.id !== jNode.id) {
              const edgeId = `te_${layer.id}_ext_h_${++edgeCounter}`;
              allEdges[edgeId] = {
                id: edgeId,
                source: jNode.id,
                target: startNode.id,
                type: layer.media,
                mediaType: layer.media,
                trayType: layer.construction,
                color: layer.color,
                widthM: layer.defaultWidthM,
              };
            }
          }

          for (let i = 0; i < junctionNodes.length - 1; i++) {
            const u = junctionNodes[i];
            const v = junctionNodes[i + 1];
            if (u.id === v.id) continue;
            const edgeId = `te_${layer.id}_spine_h_${++edgeCounter}`;
            allEdges[edgeId] = {
              id: edgeId,
              source: u.id,
              target: v.id,
              type: layer.media,
              mediaType: layer.media,
              trayType: layer.construction,
              color: layer.color,
              widthM: layer.defaultWidthM,
            };
          }
        }

        if (spineCollector === 'end' || spineCollector === 'both') {
          const endY_m = Math.max(...vRows.map(r => r.end_m));
          const endY_px = Math.round(endY_m * safePpm);

          const junctionNodes: GeneratedTrayNode[] = [];
          for (const r of vRows) {
            const x_px = Math.round(r.inRowCoord_m * safePpm);
            const jNode = registry.getOrCreateNode(
              x_px,
              endY_px,
              layerZ,
              layer.id,
              `tn_${layer.id}_${r.rowId}_end_tee`,
              'tee'
            );
            junctionNodes.push(jNode);

            // Connect shorter row end to spine T-junction if not coinciding
            const endNode = rowEndNodes[r.rowId];
            if (endNode && endNode.id !== jNode.id) {
              const edgeId = `te_${layer.id}_ext_e_${++edgeCounter}`;
              allEdges[edgeId] = {
                id: edgeId,
                source: endNode.id,
                target: jNode.id,
                type: layer.media,
                mediaType: layer.media,
                trayType: layer.construction,
                color: layer.color,
                widthM: layer.defaultWidthM,
              };
            }
          }

          for (let i = 0; i < junctionNodes.length - 1; i++) {
            const u = junctionNodes[i];
            const v = junctionNodes[i + 1];
            if (u.id === v.id) continue;
            const edgeId = `te_${layer.id}_spine_e_${++edgeCounter}`;
            allEdges[edgeId] = {
              id: edgeId,
              source: u.id,
              target: v.id,
              type: layer.media,
              mediaType: layer.media,
              trayType: layer.construction,
              color: layer.color,
              widthM: layer.defaultWidthM,
            };
          }
        }
      }
    }

    // Merge layer nodes into allNodes
    Object.assign(allNodes, registry.getAllNodes());
  }

  // Calculate metrics
  const metrics = calculatePathwayMetrics(allNodes, allEdges, safePpm);

  return {
    nodes: allNodes,
    edges: allEdges,
    metrics,
  };
}

export interface PathwayConnectivityOptions {
  byLayer?: boolean;
}

/**
 * Validates full graph connectivity using Breadth-First Search (BFS).
 * When byLayer is true (default), multi-tier presets validate that each media tier
 * is internally connected across all target racks/rows.
 */
export function validatePathwayGraphConnectivity(
  nodes: Record<string, GeneratedTrayNode>,
  edges: Record<string, GeneratedTrayEdge>,
  options: PathwayConnectivityOptions = { byLayer: true }
): PathwayConnectivityResult {
  const nodeIds = Object.keys(nodes);
  if (nodeIds.length === 0) {
    return { isFullyConnected: true, componentCount: 0, unconnectedNodes: [] };
  }

  const byLayer = options.byLayer !== false;

  if (byLayer) {
    // Partition nodes by z_m elevation (1cm tolerance)
    const layerElevations: number[] = [];
    const nodesByElevation = new Map<number, string[]>();

    for (const [id, node] of Object.entries(nodes)) {
      let matchedZ = layerElevations.find(z => Math.abs(z - node.z_m) < 0.01);
      if (matchedZ === undefined) {
        matchedZ = node.z_m;
        layerElevations.push(matchedZ);
        nodesByElevation.set(matchedZ, []);
      }
      nodesByElevation.get(matchedZ)!.push(id);
    }

    if (layerElevations.length > 1) {
      let allLayersFullyConnected = true;
      let maxComponentCount = 0;
      const allUnconnectedNodes: string[] = [];

      for (const z of layerElevations) {
        const layerNodeIds = nodesByElevation.get(z)!;
        const layerNodeSet = new Set(layerNodeIds);

        const layerNodesSubset: Record<string, GeneratedTrayNode> = {};
        for (const id of layerNodeIds) {
          layerNodesSubset[id] = nodes[id];
        }

        const layerEdgesSubset: Record<string, GeneratedTrayEdge> = {};
        for (const [eId, edge] of Object.entries(edges)) {
          if (layerNodeSet.has(edge.source) && layerNodeSet.has(edge.target)) {
            layerEdgesSubset[eId] = edge;
          }
        }

        // Run flat BFS on this single layer
        const layerResult = validatePathwayGraphConnectivity(layerNodesSubset, layerEdgesSubset, { byLayer: false });
        if (!layerResult.isFullyConnected) {
          allLayersFullyConnected = false;
        }
        if (layerResult.componentCount > maxComponentCount) {
          maxComponentCount = layerResult.componentCount;
        }
        allUnconnectedNodes.push(...layerResult.unconnectedNodes);
      }

      return {
        isFullyConnected: allLayersFullyConnected,
        componentCount: allLayersFullyConnected ? 1 : maxComponentCount,
        unconnectedNodes: allUnconnectedNodes,
      };
    }
  }

  // Build adjacency list (single layer or flat graph)
  const adj = new Map<string, string[]>();
  for (const id of nodeIds) {
    adj.set(id, []);
  }

  for (const edge of Object.values(edges)) {
    if (adj.has(edge.source) && adj.has(edge.target)) {
      adj.get(edge.source)!.push(edge.target);
      adj.get(edge.target)!.push(edge.source);
    }
  }

  const visited = new Set<string>();
  const components: string[][] = [];

  for (const id of nodeIds) {
    if (visited.has(id)) continue;

    const currentComponent: string[] = [];
    const queue = [id];
    visited.add(id);

    while (queue.length > 0) {
      const u = queue.shift()!;
      currentComponent.push(u);

      for (const v of adj.get(u) || []) {
        if (!visited.has(v)) {
          visited.add(v);
          queue.push(v);
        }
      }
    }

    components.push(currentComponent);
  }

  const componentCount = components.length;
  const isFullyConnected = componentCount <= 1;

  // Largest component is considered the primary network; others are unconnected
  components.sort((a, b) => b.length - a.length);
  const primaryComponent = components[0] || [];
  const primarySet = new Set(primaryComponent);
  const unconnectedNodes = nodeIds.filter(id => !primarySet.has(id));

  return {
    isFullyConnected,
    componentCount,
    unconnectedNodes,
  };
}

/**
 * Calculates physical summary metrics for synthesized pathways.
 */
export function calculatePathwayMetrics(
  nodes: Record<string, GeneratedTrayNode>,
  edges: Record<string, GeneratedTrayEdge>,
  ppm = DEFAULT_PPM
): PathwayGenerationMetrics {
  const edgeList = Object.values(edges);
  const nodeList = Object.values(nodes);

  // Node degree map
  const degrees = new Map<string, number>();
  for (const n of nodeList) {
    degrees.set(n.id, 0);
  }
  for (const e of edgeList) {
    degrees.set(e.source, (degrees.get(e.source) || 0) + 1);
    degrees.set(e.target, (degrees.get(e.target) || 0) + 1);
  }

  let totalLengthM = 0;
  const lengthByMediaM: Record<string, number> = {};

  for (const edge of edgeList) {
    const u = nodes[edge.source];
    const v = nodes[edge.target];
    if (!u || !v) continue;

    const dx_m = (v.x - u.x) / ppm;
    const dy_m = (v.y - u.y) / ppm;
    const distM = Math.sqrt(dx_m * dx_m + dy_m * dy_m);

    totalLengthM += distM;
    const media = edge.mediaType || 'copper';
    lengthByMediaM[media] = (lengthByMediaM[media] || 0) + distM;
  }

  let teeCount = 0;
  let terminalCount = 0;

  for (const n of nodeList) {
    const deg = degrees.get(n.id) || 0;
    if (n.type === 'tee' || deg >= 3) {
      teeCount++;
    } else if (deg <= 1) {
      terminalCount++;
    }
  }

  const connectivity = validatePathwayGraphConnectivity(nodes, edges);

  return {
    totalNodes: nodeList.length,
    totalEdges: edgeList.length,
    totalLengthM,
    lengthByMediaM,
    teeCount,
    terminalCount,
    connectedComponents: connectivity.componentCount,
    isFullyConnected: connectivity.isFullyConnected,
  };
}
